import { ChangeEvent, Fragment, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Box,
  Checkbox,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  LoadCicdProjectJobsDocument,
  useDeleteAssetsMutation,
} from "~/operations";
import { AccessTime as AccessTimeIcon } from "@mui/icons-material";
import { ClickableTableRow } from "~/components/report";
import { pluralize } from "~/lib/pluralize";
import { KeyValue, Space } from "~/lib/types";
import { useJobs } from "~/providers/jobs";
import { useTriageToolbar } from "~/providers/triage-toolbar";
import { TriageToolbarButtonType } from "~/components/TriageToolbar/types";
import {
  CiCdHeader,
  CicdProjectJobs,
  CombinedPossibleJobs,
  Job,
  JobsTypes,
  Project,
} from "~/pages/cicd/utils/cicdTypes";
import { ImpactUpdated } from "~/components/impact/Updated/impact-updated";
import { CicdSearch } from "~/components/cicd-search";
import { DataTable } from "~/components/data-table";
import {
  formattedDate,
  formattedPipelineKind,
  getComparators,
  getGitWorkflows,
  groupByJobByRelatedScans,
  isJsonString,
} from "~/pages/cicd/Jobs/CicdJobsTable/cicdJobsTableUtils";

const QUERY_TERM = "queryterms";

interface CiCdJobsTableProps {
  jobs: CicdProjectJobs["jobs"];
  space: Space;
  project: Project;
  type: JobsTypes;
  headers: CiCdHeader[];
  selectable?: boolean;
}

export function CicdJobsTable({
  jobs,
  type,
  space,
  headers,
  project,
  selectable,
}: CiCdJobsTableProps) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchFilters, setSearchFilters] = useState<KeyValue[]>([]);
  const [searchParams, _setSearchParams] = useSearchParams();
  const spaceId = searchParams.get("spaceId");
  const projectId = searchParams.get("projectId");
  const { refetch } = useJobs();
  const [selection, setSelection] = useState<string[]>([]);

  const [deleteJobs] = useDeleteAssetsMutation({
    variables: { input: { spaceMrn: space.mrn, assetMrns: selection } },
    refetchQueries: [LoadCicdProjectJobsDocument],
  });
  const triageToolbar = useTriageToolbar();
  const totalCount = jobs?.totalCount || 0;

  // Whenever the searchFilters are updated, we refetch
  // the data with our new filters
  useEffect(() => {
    refetch({ labelFilter: searchFilters });
  }, [searchFilters]);

  useEffect(() => {
    if (selection.length > 0) {
      triageToolbar.show({
        buttons: [
          {
            type: TriageToolbarButtonType.Delete,
            onClick: handleDeleteClick,
          },
        ],
        payload: {
          count: selection.length,
          totalCount,
          onClose: handleCancelClick,
        },
      });
    } else {
      triageToolbar.hide();
    }

    return () => {
      triageToolbar.hide();
    };
  }, [selection]);

  // Anytime the searchParams change, we gather everything from
  // the queryterms search param and parse it back into a format that
  // the API will accept, and then we update the search filters
  useEffect(() => {
    let nextHolding: KeyValue[] = [];
    const nextSearchFilters = searchParams.get(QUERY_TERM);
    if (nextSearchFilters) {
      nextSearchFilters.split(",").map((filter) => {
        if (isJsonString(filter)) {
          const x = JSON.parse(filter);
          nextHolding.push({
            key: `${project.type}/${Object.keys(x)[0]}`,
            value: `${Object.values(x)[0]}`,
          });
        } else {
          nextHolding.push({
            key: `${project.type}/search`,
            value: filter,
          });
        }
      });
      setSearchFilters(nextHolding);
    } else {
      setSearchFilters([]);
    }
  }, [searchParams]);

  const groupedJobs = groupByJobByRelatedScans(jobs?.edges!, (edge) =>
    getComparators(edge),
  );

  // handleQuery's job is to pick apart the filters given
  // from the CICD Search component, and deconstruct them
  // into something that will work as a URL to navigate to.
  const handleQuery = (newFilters: KeyValue[]) => {
    const reg = new RegExp(`^${project.type}\/`);
    let queryterms: string[] = [];
    newFilters.forEach((filter) => {
      const key = filter.key.replace(reg, "");
      const value = filter?.value?.replace(reg, "");
      if (key === "search" && value) {
        queryterms.push(value);
      } else {
        queryterms.push(JSON.stringify({ [key]: value }));
      }
    });
    if (queryterms.length > 0) {
      searchParams.set(QUERY_TERM, queryterms.join(","));
    } else {
      searchParams.delete(QUERY_TERM);
    }

    navigate(`${location.pathname}?${searchParams}`);
  };

  const completeBatchDelete = async () => {
    const total = selection.length;
    try {
      await deleteJobs();
      enqueueSnackbar(
        `Successfully removed ${total} ${pluralize("job", total)}`,
        { variant: "success" },
      );
      resetEditing();
    } catch (error) {
      enqueueSnackbar(`Failed to remove ${pluralize("job", total)}`, {
        variant: "error",
      });
    }
  };

  const resetEditing = () => {
    setSelection([]);
  };

  const handleCheck = (
    _event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
    mrn: string,
  ) => {
    if (checked) {
      return setSelection((prev) => [...prev, mrn]);
    }
    setSelection(selection.filter((selection) => mrn !== selection));
  };

  const handleCheckAll = () => {
    if (selection.length === 0) {
      setSelection(jobs?.edges?.map((job) => job.node.mrn) || []);
    } else {
      setSelection([]);
    }
  };

  const isJobChecked = (mrn: string) => {
    return selection.includes(mrn);
  };

  const isGroupChecked = (mrns: string[]) => {
    return mrns.every((mrn) => isJobChecked(mrn));
  };

  const isGroupIndeterminate = (mrns: string[]) => {
    if (isGroupChecked(mrns)) return false;
    return mrns.some((mrn) => isJobChecked(mrn));
  };

  const handleGroupCheckChange = (
    _event: ChangeEvent<HTMLInputElement>,
    mrns: string[],
  ) => {
    if (isGroupChecked(mrns) || isGroupIndeterminate(mrns)) {
      // uncheck all
      setSelection(selection.filter((mrn) => !mrns.includes(mrn)));
    } else {
      // check all
      setSelection([
        ...selection,
        ...mrns.filter((mrn) => !selection.includes(mrn)),
      ]);
    }
  };

  const handleDeleteClick = () => {
    completeBatchDelete();
  };

  const handleCancelClick = () => {
    resetEditing();
  };

  const onJobClick = (job: Job) =>
    navigate(
      `/space/cicd/jobs/${projectId}?spaceId=${spaceId}&projectId=${projectId}&jobId=${job.mrn}`,
    );

  const getJobFields = () => {
    switch (type) {
      case JobsTypes.Github:
        return [
          "name",
          "pipelineKind",
          "score",
          "runNumber",
          "actor",
          "updatedAt",
        ] as Array<keyof CombinedPossibleJobs>;
      case JobsTypes.Kubernetes:
        return [
          "namespace",
          "operation",
          "kind",
          "score",
          "author",
          "updatedAt",
        ] as Array<keyof CombinedPossibleJobs>;
      case JobsTypes.Jenkins:
        return ["name", "pipelineKind", "score", "jobId", "updatedAt"];
      default:
        // GitLab, CircleCI, and Azure have same fields
        return [
          "name",
          "pipelineKind",
          "score",
          "jobId",
          "userName",
          "updatedAt",
        ] as Array<keyof CombinedPossibleJobs>;
    }
  };

  const renderGitWorkflows = (job: CombinedPossibleJobs) => {
    const workflowString = getGitWorkflows(job);

    return (
      <Box sx={{ pl: 3, color: "text.secondary", overflowWrap: "anywhere" }}>
        {workflowString}
      </Box>
    );
  };

  const renderTableCellContent = (
    field: keyof CombinedPossibleJobs,
    job: CombinedPossibleJobs,
  ) => {
    if (
      (job.__typename === "GithubJob" || job.__typename === "GitlabJob") &&
      field === "name"
    ) {
      return renderGitWorkflows(job);
    }
    switch (field) {
      case "name":
        return (
          <Box
            sx={{ pl: 3, color: "text.secondary", overflowWrap: "anywhere" }}
          >
            {job.target}
          </Box>
        );
      case "pipelineKind":
        return <Box>{formattedPipelineKind(job.pipelineKind)}</Box>;
      case "namespace":
        return <Box sx={{ pl: 3, overflowWrap: "anywhere" }}>{job.name}</Box>;
      case "score":
        return (
          <ImpactUpdated
            impact={{
              value: job.score.riskValue,
              rating: job.score.riskRating,
            }}
            isRiskScore
          />
        );
      case "updatedAt":
        return (
          <Box sx={{ fontSize: 12 }}>
            <AccessTimeIcon sx={{ mr: 1, fontSize: "inherit" }} />
            {formattedDate(job.updatedAt)}
          </Box>
        );
      default:
        return <Box>{job[field]}</Box>;
    }
  };

  return (
    <>
      <Box id="jobs-list-header" sx={{ mb: 4 }}>
        <CicdSearch
          onQuery={handleQuery}
          project={project}
          spaceMrn={space.mrn}
          filters={searchFilters}
          projectId={project.projectID}
        />
      </Box>
      <DataTable id="jobs-list" selectable={selectable} selection={selection}>
        <TableHead>
          <TableRow>
            {selectable && (
              <TableCell key={`cicd-jobs-${projectId}-checkbox`}>
                <Checkbox
                  checked={selection.length === totalCount}
                  indeterminate={
                    selection.length > 0 && selection.length < totalCount
                  }
                  onChange={handleCheckAll}
                />
              </TableCell>
            )}
            {headers.map((header: CiCdHeader) => (
              <TableCell key={`cicd-jobs-${projectId}-${header.label}`}>
                {header.label}
              </TableCell>
            ))}
            <TableCell sortDirection={"desc"} width={220}>
              <TableSortLabel direction={"desc"} active={true}>
                Time
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groupedJobs.map((group) => {
            const groupMrns = group.map((job) => job.node.mrn);
            return group.map((edge, index) => {
              const job = edge.node as CombinedPossibleJobs;
              const isSelected = isJobChecked(job.mrn);
              const className =
                (isSelected ? "selected" : "") + " jobs-list-row";
              return (
                <Fragment key={`${job.runId}-${index}`}>
                  {/* If this group contains multiple scans, we'll provide a header and then indent the scans */}
                  {index === 0 && (
                    <TableRow>
                      {selectable && (
                        <TableCell
                          sx={{
                            borderBottom: (theme) =>
                              `1px solid ${theme.palette.background.light}`,
                          }}
                        >
                          <Checkbox
                            checked={isGroupChecked(groupMrns)}
                            indeterminate={isGroupIndeterminate(groupMrns)}
                            onChange={(event) =>
                              handleGroupCheckChange(event, groupMrns)
                            }
                          />
                        </TableCell>
                      )}
                      <TableCell
                        colSpan={headers.length + 1}
                        sx={{
                          borderBottom: (theme) =>
                            `1px solid ${theme.palette.background.light}`,
                        }}
                      >
                        {job.__typename === "KubernetesJob"
                          ? job.namespace
                          : job.name}
                      </TableCell>
                    </TableRow>
                  )}
                  <ClickableTableRow
                    key={job.id}
                    className={className}
                    onClick={() => onJobClick(job)}
                  >
                    {selectable && (
                      <TableCell
                        key={`cicd-jobs-${projectId}-${job.id}-checkbox`}
                      >
                        <Checkbox
                          onChange={(e, checked) =>
                            handleCheck(e, checked, edge.node.mrn)
                          }
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                    )}
                    {getJobFields().map((field) => {
                      return (
                        <TableCell
                          key={`cicd-jobs-${projectId}-${job.id}-${field}`}
                        >
                          {renderTableCellContent(
                            field as keyof CombinedPossibleJobs,
                            job,
                          )}
                        </TableCell>
                      );
                    })}
                  </ClickableTableRow>
                </Fragment>
              );
            });
          })}
        </TableBody>
      </DataTable>
    </>
  );
}
